import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { Search, Plus, Edit2, Trash2, Tag, TrendingUp, X, CheckCircle, XCircle, Clock, Percent, DollarSign, Activity } from 'lucide-react';
import toast from 'react-hot-toast';

const Coupons = () => {
  const { colors } = useTheme();
  const [coupons, setCoupons] = useState([]);
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    maxDiscountAmount: '',
    minBookingAmount: '',
    usageLimit: '',
    perUserLimit: '1',
    startDate: '',
    endDate: '',
    status: 'active',
    isActive: true
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  useEffect(() => {
    filterCoupons();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupons, searchTerm, filterStatus, filterType]);

  const fetchCoupons = async () => {
    try {
      const couponsQuery = query(collection(db, 'coupons'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(couponsQuery);
      const couponsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startDate: data.validityPeriod?.startDate?.toDate?.() || null,
          endDate: data.validityPeriod?.endDate?.toDate?.() || null,
          minBookingAmount: data.restrictions?.minBookingAmount || 0,
          createdAt: data.createdAt?.toDate?.() || null,
        };
      });
      setCoupons(couponsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('Failed to fetch coupons');
      setLoading(false);
    }
  };

  const filterCoupons = () => {
    let filtered = coupons;

    if (searchTerm) {
      filtered = filtered.filter(coupon =>
        coupon.code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(coupon => {
        const now = new Date();
        if (filterStatus === 'active') {
          return coupon.status === 'active' && 
                 coupon.isActive && 
                 (!coupon.endDate || coupon.endDate > now);
        } else if (filterStatus === 'inactive') {
          return coupon.status === 'inactive' || !coupon.isActive;
        } else if (filterStatus === 'expired') {
          return coupon.endDate && coupon.endDate < now;
        }
        return true;
      });
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(coupon => coupon.discountType === filterType);
    }

    setFilteredCoupons(filtered);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discountType: 'percentage',
      discountValue: '',
      maxDiscountAmount: '',
      minBookingAmount: '',
      usageLimit: '',
      perUserLimit: '1',
      startDate: '',
      endDate: '',
      status: 'active',
      isActive: true
    });
    setEditingCoupon(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue?.toString() || '',
      maxDiscountAmount: coupon.maxDiscountAmount?.toString() || '',
      minBookingAmount: coupon.minBookingAmount?.toString() || '',
      usageLimit: coupon.usageLimit?.toString() || '',
      perUserLimit: coupon.perUserLimit?.toString() || '1',
      startDate: coupon.startDate ? coupon.startDate.toISOString().split('T')[0] : '',
      endDate: coupon.endDate ? coupon.endDate.toISOString().split('T')[0] : '',
      status: coupon.status,
      isActive: coupon.isActive
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code.trim()) {
      toast.error('Coupon code is required');
      return;
    }

    if (!formData.discountValue || parseFloat(formData.discountValue) <= 0) {
      toast.error('Discount value must be greater than 0');
      return;
    }

    if (formData.discountType === 'percentage' && parseFloat(formData.discountValue) > 100) {
      toast.error('Percentage discount cannot exceed 100%');
      return;
    }

    try {
      const couponData = {
        code: formData.code.toUpperCase().trim(),
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        perUserLimit: formData.perUserLimit ? parseInt(formData.perUserLimit) : 1,
        validityPeriod: {
          startDate: formData.startDate ? Timestamp.fromDate(new Date(formData.startDate)) : null,
          endDate: formData.endDate ? Timestamp.fromDate(new Date(formData.endDate)) : null,
        },
        restrictions: {
          minBookingAmount: formData.minBookingAmount ? parseFloat(formData.minBookingAmount) : 0,
        },
        status: formData.status,
        isActive: formData.isActive,
        updatedAt: Timestamp.now()
      };

      if (editingCoupon) {
        const couponRef = doc(db, 'coupons', editingCoupon.id);
        await updateDoc(couponRef, couponData);
        toast.success('Coupon updated successfully');
      } else {
        couponData.createdAt = Timestamp.now();
        couponData.currentUsage = 0;
        await addDoc(collection(db, 'coupons'), couponData);
        toast.success('Coupon created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);
      toast.error(`Failed to ${editingCoupon ? 'update' : 'create'} coupon`);
    }
  };

  const handleDelete = async (couponId, couponCode) => {
    if (!window.confirm(`Are you sure you want to delete coupon "${couponCode}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'coupons', couponId));
      toast.success('Coupon deleted successfully');
      fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('Failed to delete coupon');
    }
  };

  const toggleStatus = async (coupon) => {
    try {
      const newStatus = !coupon.isActive;
      await updateDoc(doc(db, 'coupons', coupon.id), {
        isActive: newStatus,
        status: newStatus ? 'active' : 'inactive',
        updatedAt: Timestamp.now()
      });
      toast.success(`Coupon ${newStatus ? 'activated' : 'deactivated'}`);
      fetchCoupons();
    } catch (error) {
      console.error('Error toggling coupon status:', error);
      toast.error('Failed to update coupon status');
    }
  };

  const getStatusBadge = (coupon) => {
    const now = new Date();
    if (coupon.endDate && coupon.endDate < now) {
      return (
        <span 
          className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase border text-gray-500 bg-gray-500/10 border-gray-500/20"
        >
          <Clock className="w-3 h-3 mr-1" /> Expired
        </span>
      );
    }
    if (!coupon.isActive || coupon.status === 'inactive') {
      return (
        <span 
          className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase border"
          style={{ backgroundColor: colors.errorGhost, color: colors.error, borderColor: `${colors.error}30` }}
        >
          <XCircle className="w-3 h-3 mr-1" /> Disabled
        </span>
      );
    }
    return (
      <span 
        className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase border"
        style={{ backgroundColor: colors.successGhost, color: colors.success, borderColor: `${colors.success}30` }}
      >
        <CheckCircle className="w-3 h-3 mr-1" /> Active
      </span>
    );
  };

  const formatDiscount = (coupon) => {
    if (coupon.discountType === 'percentage') {
      return (
        <div className="flex flex-col">
          <span className="text-[15px] font-black" style={{ color: colors.primary }}>{coupon.discountValue}% OFF</span>
          {coupon.maxDiscountAmount && (
            <span className="text-[10px] uppercase font-bold tracking-wider mt-0.5" style={{ color: colors.textMuted }}>
              Max SAR {coupon.maxDiscountAmount.toFixed(0)}
            </span>
          )}
        </div>
      );
    }
    return (
      <span className="text-[15px] font-black" style={{ color: colors.success }}>
        SAR {coupon.discountValue?.toFixed(0) || '0'}
      </span>
    );
  };

  const kpis = [
    { id: 'all', label: 'Total Coupons', value: coupons.length, icon: Tag, color: colors.text, gradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' },
    { id: 'active', label: 'Active Codes', value: coupons.filter(c => c.isActive && c.status === 'active' && (!c.endDate || c.endDate > new Date())).length, icon: Activity, color: colors.success, gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
    { id: 'uses', label: 'Lifetime Uses', value: coupons.reduce((sum, c) => sum + (c.currentUsage || 0), 0), icon: TrendingUp, color: colors.primary, gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' },
    { id: 'expired', label: 'Expired', value: coupons.filter(c => c.endDate && c.endDate < new Date()).length, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Promo Codes
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Manage discount campaigns, restrictions, and coupon redemptions.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center px-5 py-2.5 rounded-xl font-bold text-[13px] text-white shadow-md hover:-translate-y-0.5 transition-all duration-300"
          style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' }}
        >
          <Plus className="w-4 h-4 mr-2" /> CREATE COUPON
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <div
            key={kpi.id}
            className={`card-premium p-4 transition-all opacity-0 animate-fadeInScale stagger-${i+1}`}
            style={{
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`
            }}
          >
            <div className="flex flex-col h-full justify-between gap-3">
              <div className="flex items-center justify-between">
                <div 
                  className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm"
                  style={{ background: kpi.gradient }}
                >
                  <kpi.icon className="w-[18px] h-[18px] text-white" />
                </div>
                <p className="text-2xl font-bold" style={{ color: colors.text }}>{kpi.value.toLocaleString()}</p>
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>{kpi.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Modern Filter Row */}
      <div className="card-premium p-4 md:p-5" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[16px] h-[16px]" style={{ color: colors.textMuted }} />
            <input
              type="text"
              placeholder="Search by code (e.g., SUMMER20..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-[13px] font-medium focus:outline-none transition-all uppercase tracking-wide"
              style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
              onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          
          <div className="flex gap-4">
             <select
               className="px-4 py-2.5 rounded-xl text-[13px] font-medium appearance-none min-w-[140px] focus:outline-none transition-all cursor-pointer"
               style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
               value={filterStatus}
               onChange={(e) => setFilterStatus(e.target.value)}
               onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
               onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
             >
               <option value="all">Status: All</option>
               <option value="active">Active</option>
               <option value="inactive">Disabled</option>
               <option value="expired">Expired</option>
             </select>
             
             <select
               className="px-4 py-2.5 rounded-xl text-[13px] font-medium appearance-none min-w-[140px] focus:outline-none transition-all cursor-pointer"
               style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
               value={filterType}
               onChange={(e) => setFilterType(e.target.value)}
               onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
               onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
             >
               <option value="all">Type: All</option>
               <option value="percentage">Percentage (%)</option>
               <option value="fixed">Fixed Amount (SAR)</option>
             </select>
          </div>
        </div>
      </div>

      {/* Table Data */}
      <div className="card-premium overflow-hidden animate-fadeInScale" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        {loading ? (
             <div className="flex flex-col items-center justify-center p-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
                <p className="mt-4 text-[14px] font-medium" style={{ color: colors.textSecondary }}>Refreshing promotions...</p>
             </div>
        ) : filteredCoupons.length === 0 ? (
             <div className="flex flex-col items-center justify-center p-16">
               <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: colors.backgroundSecondary }}>
                  <Tag className="w-8 h-8" style={{ color: colors.textMuted }} />
               </div>
               <h3 className="text-[15px] font-bold" style={{ color: colors.text }}>No coupons found</h3>
               <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>Adjust filters or create a new coupon code.</p>
             </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Promotional Code</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Discount Reward</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Campaign Limits</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Dates & Context</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: colors.border }}>
                {filteredCoupons.map((coupon) => (
                  <tr 
                    key={coupon.id} 
                    className="hover:bg-opacity-50 transition-colors"
                    style={{ backgroundColor: colors.surface }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                       <div className="flex items-center gap-3">
                           <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/10 text-blue-500 border border-blue-500/20">
                              {coupon.discountType === 'percentage' ? <Percent className="w-5 h-5"/> : <DollarSign className="w-5 h-5"/>}
                           </div>
                           <div>
                              <span className="font-mono text-[14px] font-black uppercase tracking-wider" style={{ color: colors.text }}>{coupon.code}</span>
                              <div className="mt-1">{getStatusBadge(coupon)}</div>
                           </div>
                       </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       {formatDiscount(coupon)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       <div className="text-[12px] font-bold" style={{ color: colors.textSecondary }}>
                          Min Cart: <span style={{ color: colors.text }}>{coupon.minBookingAmount ? `SAR ${coupon.minBookingAmount}` : 'None'}</span>
                       </div>
                       <div className="mt-1 flex items-center gap-1.5 text-[12px] font-bold" style={{ color: colors.textSecondary }}>
                          Redeemed: <span style={{ color: colors.primary }}>{coupon.currentUsage || 0}</span> / {coupon.usageLimit || '∞'}
                       </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                       <div className="text-[12px] font-bold" style={{ color: colors.textSecondary }}>
                          Starts: <span style={{ color: colors.text }}>{coupon.startDate ? new Date(coupon.startDate).toLocaleDateString() : 'Active Now'}</span>
                       </div>
                       <div className="text-[12px] font-bold mt-1" style={{ color: colors.textSecondary }}>
                          Expires: <span style={{ color: colors.error }}>{coupon.endDate ? new Date(coupon.endDate).toLocaleDateString() : 'Never'}</span>
                       </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                       <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => toggleStatus(coupon)}
                            className="p-2 rounded-xl transition-all hover:scale-105 shadow-sm border"
                            style={{ backgroundColor: colors.background, color: coupon.isActive ? colors.warning : colors.success, borderColor: colors.border }}
                            title={coupon.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {coupon.isActive ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => openEditModal(coupon)}
                            className="p-2 rounded-xl transition-all hover:scale-105 shadow-sm border"
                            style={{ backgroundColor: colors.background, color: colors.primary, borderColor: colors.border }}
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(coupon.id, coupon.code)}
                            className="p-2 rounded-xl transition-all hover:scale-105 shadow-sm border hover:bg-red-500/10"
                            style={{ backgroundColor: colors.background, color: colors.error, borderColor: colors.border }}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                       </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor Mega Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
            <div className="rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative animate-fadeInScale flex flex-col" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            
            <div className="sticky top-0 z-10 px-6 py-5 border-b flex items-center justify-between" style={{ backgroundColor: colors.surfaceHover, borderColor: colors.border }}>
               <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                     <Tag className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black tracking-tight" style={{ color: colors.text }}>
                      {editingCoupon ? 'Configure Campaign' : 'New Promotional Code'}
                    </h2>
                    <p className="text-[11px] font-bold uppercase tracking-wider mt-0.5" style={{ color: colors.textSecondary }}>Rule parameters & boundaries</p>
                  </div>
               </div>
               <button onClick={() => setShowModal(false)} className="p-2 rounded-full hover:rotate-90 transition-all duration-300 bg-white/5 shadow-sm" style={{ backgroundColor: colors.backgroundSecondary }}>
                 <X className="w-5 h-5" style={{ color: colors.text }} />
               </button>
            </div>

            <div className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Promo Code Block */}
                <div className="p-5 rounded-2xl border bg-gradient-to-br from-blue-500/5 to-indigo-500/5" style={{ borderColor: `${colors.primary}20` }}>
                   <label className="block text-[11px] font-extrabold uppercase tracking-widest mb-2" style={{ color: colors.primary }}>Marketing Alias / Code *</label>
                   <input
                     type="text"
                     required
                     className="w-full px-5 py-4 rounded-xl text-lg font-black font-mono tracking-widest uppercase focus:outline-none transition-all shadow-sm"
                     style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                     value={formData.code}
                     onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                     placeholder="E.G. RAMADAN2025"
                     onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                     onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                   />
                </div>

                {/* Reward Configuration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                   <div className="p-5 rounded-2xl border" style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                      <label className="block text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: colors.textSecondary }}>Discount Method *</label>
                      <select
                        required
                        className="w-full px-4 py-3 rounded-xl text-[13px] font-medium focus:outline-none transition-all appearance-none cursor-pointer"
                        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}
                        value={formData.discountType}
                        onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                        onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                        onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                      >
                        <option value="percentage">Percentage Off (%)</option>
                        <option value="fixed">Fixed Deduction (SAR)</option>
                      </select>
                   </div>
                   <div className="p-5 rounded-2xl border relative overflow-hidden" style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                      <div className="absolute top-0 left-0 w-1 h-full bg-green-500"></div>
                      <label className="block text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: colors.textSecondary }}>Reward Volume *</label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-3 rounded-xl text-[15px] font-black focus:outline-none transition-all"
                        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}
                        value={formData.discountValue}
                        onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                        placeholder={formData.discountType === 'percentage' ? '0-100' : '0.00'}
                        onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                        onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                      />
                   </div>
                </div>

                {/* Additional Boundaries */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                   {formData.discountType === 'percentage' && (
                     <div>
                       <label className="block text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.warning }}>Maximum Absolute Deduction (SAR)</label>
                       <p className="text-[10px] mb-2 leading-tight" style={{ color: colors.textMuted }}>Prevents giant payouts on luxury bookings.</p>
                       <input
                         type="number"
                         min="0"
                         step="0.01"
                         className="w-full px-4 py-3 rounded-xl text-[13px] font-medium focus:outline-none transition-all"
                         style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                         value={formData.maxDiscountAmount}
                         onChange={(e) => setFormData({ ...formData, maxDiscountAmount: e.target.value })}
                         placeholder="Optional Cap limit"
                         onFocus={(e) => { e.target.style.borderColor = colors.warning; e.target.style.boxShadow = `0 0 0 3px ${colors.warning}30`; }}
                         onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                       />
                     </div>
                   )}
                   <div>
                     <label className="block text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textSecondary }}>Minimum Cart Value To Qualify (SAR)</label>
                     <p className="text-[10px] mb-2 leading-tight opacity-0 pointer-events-none" style={{ color: colors.textMuted }}>Padding placeholder</p> {/* Alignment spacer */}
                     <input
                       type="number"
                       min="0"
                       step="0.01"
                       className="w-full px-4 py-3 rounded-xl text-[13px] font-medium focus:outline-none transition-all"
                       style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                       value={formData.minBookingAmount}
                       onChange={(e) => setFormData({ ...formData, minBookingAmount: e.target.value })}
                       placeholder="Zero to allow any cart size"
                       onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                       onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                     />
                   </div>
                </div>

                {/* Utilization Triggers */}
                <div className="p-5 rounded-2xl border" style={{ backgroundColor: colors.backgroundSecondary, borderColor: colors.border }}>
                   <p className="text-[11px] font-bold uppercase tracking-wider mb-4" style={{ color: colors.textSecondary }}>Campaign Supply Chain Constraints</p>
                   <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider mb-2 font-semibold" style={{ color: colors.textMuted }}>Total Pool Allowed Redemptions</label>
                        <input
                          type="number"
                          min="1"
                          className="w-full px-4 py-3 rounded-xl text-[13px] font-medium focus:outline-none transition-all"
                          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}
                          value={formData.usageLimit}
                          onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                          placeholder="Infinite if blank"
                          onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                          onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] uppercase tracking-wider mb-2 font-semibold" style={{ color: colors.textMuted }}>Max Invocations Per Guest Account</label>
                        <input
                          type="number"
                          min="1"
                          className="w-full px-4 py-3 rounded-xl text-[13px] font-medium focus:outline-none transition-all"
                          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}
                          value={formData.perUserLimit}
                          onChange={(e) => setFormData({ ...formData, perUserLimit: e.target.value })}
                          placeholder="Default 1"
                          onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                          onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>
                   </div>
                </div>

                {/* Date Ranges */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: colors.textSecondary }}>Campaign Start Date Targeting</label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 rounded-xl text-[13px] font-medium focus:outline-none transition-all"
                      style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: colors.textSecondary }}>Sunset Expiration Horizon</label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 rounded-xl text-[13px] font-medium focus:outline-none transition-all"
                      style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    />
                  </div>
                </div>

                <div className="pt-6 border-t flex justify-end gap-3" style={{ borderColor: colors.border }}>
                   <button
                     type="button"
                     onClick={() => setShowModal(false)}
                     className="px-6 py-3 rounded-xl text-[13px] font-bold transition-all"
                     style={{ backgroundColor: colors.backgroundSecondary, color: colors.text }}
                   >
                     Cancel
                   </button>
                   <button
                     type="submit"
                     className="px-8 py-3 rounded-xl text-[13px] font-bold transition-all text-white shadow-md hover:-translate-y-0.5"
                     style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' }}
                   >
                     Deploy Live
                   </button>
                </div>

              </form>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default Coupons;
